// Flash Message Service
// This service provides a global way to show flash messages across the application

import { ref } from 'vue';

// Create reactive references for each message type
const message = ref(null);
const success = ref(null);
const warning = ref(null);
const danger = ref(null);

// Default auto-hide delay in milliseconds (10 seconds minimum as per requirements)
const DEFAULT_AUTO_HIDE_DELAY = 10000;

// Store timeouts to be able to clear them if needed
const timeouts = {
  message: null,
  success: null,
  warning: null,
  danger: null
};

/**
 * Show a message of the specified type
 * @param {string} type - The message type ('message', 'success', 'warning', 'danger')
 * @param {string} text - The message text
 * @param {boolean} autoHide - Whether to automatically hide the message
 * @param {number} delay - The delay in milliseconds before hiding the message
 */
function showMessage(type, text, autoHide = true, delay = DEFAULT_AUTO_HIDE_DELAY) {
  // Ensure minimum delay of 10 seconds
  const actualDelay = Math.max(delay, 10000);

  // Clear any existing timeout for this message type
  if (timeouts[type]) {
    clearTimeout(timeouts[type]);
    timeouts[type] = null;
  }

  // If the message is the same as the current one, temporarily clear it to create a visual "refresh" effect
  const currentValue =
    type === 'message' ? message.value :
    type === 'success' ? success.value :
    type === 'warning' ? warning.value :
    type === 'danger' ? danger.value : null;

  if (currentValue === text) {
    // Temporarily clear the message to create a visual "refresh" effect
    if (type === 'message') message.value = null;
    if (type === 'success') success.value = null;
    if (type === 'warning') warning.value = null;
    if (type === 'danger') danger.value = null;

    // Use setTimeout with a small delay to set the message again
    setTimeout(() => {
      if (type === 'message') message.value = text;
      if (type === 'success') success.value = text;
      if (type === 'warning') warning.value = text;
      if (type === 'danger') danger.value = text;

      // Reset the auto-hide timer to ensure the message stays visible for the full duration
      if (autoHide) {
        if (timeouts[type]) {
          clearTimeout(timeouts[type]);
          timeouts[type] = null;
        }

        timeouts[type] = setTimeout(() => {
          removeMessage(type);
          timeouts[type] = null;
        }, actualDelay);
      }
    }, 100);
  } else {
    // Set the message directly if it's different from the current one
    if (type === 'message') message.value = text;
    if (type === 'success') success.value = text;
    if (type === 'warning') warning.value = text;
    if (type === 'danger') danger.value = text;
  }

  // Auto-hide the message if enabled
  if (autoHide) {
    timeouts[type] = setTimeout(() => {
      removeMessage(type);
      timeouts[type] = null;
    }, actualDelay);
  }
}

/**
 * Remove a message of the specified type
 * @param {string} type - The message type ('message', 'success', 'warning', 'danger')
 */
function removeMessage(type) {
  if (type === 'message') message.value = null;
  if (type === 'success') success.value = null;
  if (type === 'warning') warning.value = null;
  if (type === 'danger') danger.value = null;
}

/**
 * Show a success message
 * @param {string} text - The message text
 * @param {boolean} autoHide - Whether to automatically hide the message
 * @param {number} delay - The delay in milliseconds before hiding the message
 */
function showSuccess(text, autoHide = true, delay = DEFAULT_AUTO_HIDE_DELAY) {
  showMessage('success', text, autoHide, delay);
}

/**
 * Show an info message
 * @param {string} text - The message text
 * @param {boolean} autoHide - Whether to automatically hide the message
 * @param {number} delay - The delay in milliseconds before hiding the message
 */
function showInfo(text, autoHide = true, delay = DEFAULT_AUTO_HIDE_DELAY) {
  showMessage('message', text, autoHide, delay);
}

/**
 * Show a warning message
 * @param {string} text - The message text
 * @param {boolean} autoHide - Whether to automatically hide the message
 * @param {number} delay - The delay in milliseconds before hiding the message
 */
function showWarning(text, autoHide = true, delay = DEFAULT_AUTO_HIDE_DELAY) {
  showMessage('warning', text, autoHide, delay);
}

/**
 * Show a danger/error message
 * @param {string} text - The message text
 * @param {boolean} autoHide - Whether to automatically hide the message
 * @param {number} delay - The delay in milliseconds before hiding the message
 */
function showDanger(text, autoHide = true, delay = DEFAULT_AUTO_HIDE_DELAY) {
  showMessage('danger', text, autoHide, delay);
}

// Export the service
export default {
  // State
  message,
  success,
  warning,
  danger,

  // Methods
  showMessage,
  removeMessage,
  showSuccess,
  showInfo,
  showWarning,
  showDanger
};
